import React from 'react';
import { Group, Button } from '@mantine/core';
import { HiOutlinePencilAlt, HiOutlineSave } from 'react-icons/hi';
import { useFormStatus } from 'providers/FormProviders';

interface FormActionsProps {
  onCancel?: () => void;
  onSaveAsDraft?: () => void;
  cancelText?: string;
  saveAsDraftText?: string;
  submitText?: string;
  mode?: 'create' | 'edit' | 'add';
  showSaveAsDraft?: boolean;
  showCancel?: boolean;
  cancelVariant?: 'transparent' | 'subtle' | 'outline' | 'filled';
  cancelColor?: string;
  customActions?: React.ReactNode;
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
}

/**
 * Reusable form actions component with form status integration
 * Handles submit, save as draft, and cancel actions with proper loading states
 */
export function FormActions({
  onCancel,
  onSaveAsDraft,
  cancelText = 'Cancel',
  saveAsDraftText = 'Save as Draft',
  submitText,
  mode = 'create',
  showSaveAsDraft = true,
  showCancel = true,
  cancelVariant = 'transparent',
  cancelColor = 'destructive',
  customActions,
  justify = 'space-between'
}: FormActionsProps) {
  const { isSubmitting } = useFormStatus();

  const defaultSubmitText = mode === 'edit' ? 'Update' : 'Submit';
  const submitIcon = mode === 'edit' ? <HiOutlinePencilAlt /> : <HiOutlineSave />;

  return (
    <Group justify={justify} mt={24}>
      {/* Left side actions */}
      {showCancel && (
        <Button variant={cancelVariant} c={cancelColor} onClick={onCancel} disabled={isSubmitting}>
          {cancelText}
        </Button>
      )}

      {/* Custom actions slot */}
      {customActions}

      {/* Right side actions */}
      <Group>
        {showSaveAsDraft && onSaveAsDraft && (
          <Button variant='light' onClick={onSaveAsDraft} disabled={isSubmitting}>
            {saveAsDraftText}
          </Button>
        )}

        <Button
          type='submit'
          loading={isSubmitting}
          disabled={isSubmitting}
          leftSection={submitIcon}
        >
          {submitText || defaultSubmitText}
        </Button>
      </Group>
    </Group>
  );
}

/**
 * Simple submit button with form status integration
 */
interface FormSubmitButtonProps {
  children?: React.ReactNode;
  mode?: 'create' | 'edit' | 'add';
  variant?: 'filled' | 'light' | 'outline' | 'subtle';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  color?: string;
}

export function FormSubmitButton({
  children,
  mode = 'create',
  variant = 'filled',
  size = 'md',
  fullWidth = false,
  color
}: FormSubmitButtonProps) {
  const { isSubmitting, canSubmit } = useFormStatus();

  const defaultText = mode === 'edit' ? 'Update' : 'Submit';
  const submitIcon = mode === 'edit' ? <HiOutlinePencilAlt /> : <HiOutlineSave />;

  return (
    <Button
      type='submit'
      loading={isSubmitting}
      disabled={!canSubmit}
      leftSection={submitIcon}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      color={color}
    >
      {children || defaultText}
    </Button>
  );
}

/**
 * Cancel button with consistent styling
 */
interface FormCancelButtonProps {
  onClick?: () => void;
  children?: React.ReactNode;
  variant?: 'transparent' | 'subtle' | 'outline' | 'filled';
  color?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function FormCancelButton({
  onClick,
  children = 'Cancel',
  variant = 'transparent',
  color = 'destructive',
  size = 'md'
}: FormCancelButtonProps) {
  const { isSubmitting } = useFormStatus();

  return (
    <Button variant={variant} c={color} onClick={onClick} disabled={isSubmitting} size={size}>
      {children}
    </Button>
  );
}

/**
 * Save as draft button with consistent styling
 */
interface FormSaveAsDraftButtonProps {
  onClick?: () => void;
  children?: React.ReactNode;
  variant?: 'light' | 'outline' | 'subtle' | 'filled';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function FormSaveAsDraftButton({
  onClick,
  children = 'Save as Draft',
  variant = 'light',
  size = 'md'
}: FormSaveAsDraftButtonProps) {
  const { isSubmitting } = useFormStatus();

  return (
    <Button variant={variant} onClick={onClick} disabled={isSubmitting} size={size}>
      {children}
    </Button>
  );
}

export default FormActions;
